import type { ExerciseType } from '../../../types/exercise';
import type { TrainingLevel } from '../../../types/registration';
import type {
  TrainingBlock,
  TrainingBlockExercise,
  TrainingExerciseExecution,
  TrainingMuscleVolume,
  TrainingSummaryMetric,
} from '../../../types/training';
import { countsTowardTrainingVolume } from '../../../utils/trainingVolume';
import { getPyramidLoadPercentageValue } from '../../../utils/pyramidLoadPercentage';
import { usesPrescribedLoadPercentage } from '../../../utils/trainingExecutionMethods';
import { resolvePrimaryWorkoutLevel } from '../../../utils/workoutTrainingLevels';

export const TRAINING_MUSCLE_GROUP_LABELS = [
  'Peitoral',
  'Costas / Dorsais',
  'Ombros / Deltoides',
  'Bíceps',
  'Tríceps',
  'Antebraços',
  'Trapézio',
  'Core / Abdômen',
  'Lombar',
  'Glúteos',
  'Quadríceps',
  'Posteriores de Coxa / Isquiotibiais',
  'Adutores',
  'Abdutores',
  'Panturrilhas',
] as const;

const LEVEL_LOAD_BASE: Record<TrainingLevel, number> = {
  beginner: 45,
  intermediate: 60,
  advanced: 75,
  very_advanced: 82,
};

const METHOD_LOAD_FACTOR: Record<string, number> = {
  bi_set: 1.12,
  drop_set: 1.2,
  rest_pause: 1.16,
};

const EXERCISE_GROUP_TO_MUSCLE_GROUPS: Record<string, string[]> = {
  peitoral: ['Peitoral'],
  ombro: ['Ombros / Deltoides'],
  'ombros / deltoides': ['Ombros / Deltoides'],
  'costas/dorsal': ['Costas / Dorsais'],
  'costas / dorsais': ['Costas / Dorsais'],
  biceps: ['Bíceps'],
  'bíceps': ['Bíceps'],
  triceps: ['Tríceps'],
  'tríceps': ['Tríceps'],
  pernas: [
    'Quadríceps',
    'Posteriores de Coxa / Isquiotibiais',
    'Glúteos',
    'Adutores',
    'Abdutores',
    'Panturrilhas',
  ],
};

const MUSCLE_GROUP_ALIASES: Record<string, string> = {
  peitoral: 'Peitoral',
  'costas / dorsais': 'Costas / Dorsais',
  'costas/dorsais': 'Costas / Dorsais',
  'costas / dorsal': 'Costas / Dorsais',
  'costas/dorsal': 'Costas / Dorsais',
  'ombros / deltoides': 'Ombros / Deltoides',
  ombro: 'Ombros / Deltoides',
  ombros: 'Ombros / Deltoides',
  biceps: 'Bíceps',
  'bíceps': 'Bíceps',
  triceps: 'Tríceps',
  'tríceps': 'Tríceps',
  antebracos: 'Antebraços',
  'antebraços': 'Antebraços',
  trapezio: 'Trapézio',
  'trapézio': 'Trapézio',
  'core / abdomen': 'Core / Abdômen',
  'core / abdômen': 'Core / Abdômen',
  abdomen: 'Core / Abdômen',
  'abdômen': 'Core / Abdômen',
  lombar: 'Lombar',
  gluteos: 'Glúteos',
  'glúteos': 'Glúteos',
  quadriceps: 'Quadríceps',
  'quadríceps': 'Quadríceps',
  'posteriores de coxa / isquiotibiais': 'Posteriores de Coxa / Isquiotibiais',
  adutores: 'Adutores',
  abdutores: 'Abdutores',
  panturrilhas: 'Panturrilhas',
};

const normalizeText = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const clamp = (value: number, minValue: number, maxValue: number) => Math.min(Math.max(value, minValue), maxValue);

const parsePositiveInteger = (value: string | number | null | undefined, fallbackValue: number) => {
  const parsedValue = typeof value === 'number'
    ? Math.trunc(value)
    : Number.parseInt((value ?? '').replace(/\D/g, ''), 10);

  if (Number.isNaN(parsedValue) || parsedValue <= 0) {
    return fallbackValue;
  }

  return parsedValue;
};

const normalizeMethodName = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  return trimmedValue;
};

const resolveMethodLoadFactor = (value?: string | null) => METHOD_LOAD_FACTOR[normalizeMethodName(value)] ?? 1;

const normalizeMuscleGroupLabel = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return null;
  }

  return MUSCLE_GROUP_ALIASES[normalizeText(trimmedValue)] ?? trimmedValue;
};

const resolveExecutionDetails = (execution: TrainingExerciseExecution) => execution.details ?? [];

const resolveVolumeSets = (execution: TrainingExerciseExecution) => (
  Math.max(resolveExecutionDetails(execution).length, parsePositiveInteger(execution.sets, 3))
);

const resolveVolumeContribution = (exercise: TrainingBlockExercise) => {
  if (!countsTowardTrainingVolume(exercise.execution.exerciseType)) {
    return 0;
  }

  return resolveVolumeSets(exercise.execution);
};

const parseOptionalLoadPercentage = (value?: string | number | null) => {
  const parsedValue = typeof value === 'number'
    ? Math.trunc(value)
    : Number.parseInt(value?.replace(/\D/g, '') ?? '', 10);

  if (!Number.isInteger(parsedValue) || parsedValue < 0 || parsedValue > 100) {
    return null;
  }

  return parsedValue;
};

const resolveExerciseLoadValues = (exercise: TrainingBlockExercise) => {
  const details = resolveExecutionDetails(exercise.execution);
  const method = exercise.execution.method;

  if (details.length > 0) {
    return details
      .map((detail) => (
        usesPrescribedLoadPercentage(method)
          ? getPyramidLoadPercentageValue(detail.repetitionsMax ?? detail.repetitionsMin)
          : parseOptionalLoadPercentage(detail.loadPercentage)
      ))
      .filter((value): value is number => value !== null);
  }

  if (!usesPrescribedLoadPercentage(method)) {
    return [];
  }

  const prescribedLoad = getPyramidLoadPercentageValue(exercise.execution.repetitions);

  return prescribedLoad != null ? [prescribedLoad] : [];
};

type TrainingLevelInput = TrainingLevel | TrainingLevel[] | '';

const resolveLoadTrainingLevel = (trainingLevel: TrainingLevelInput): TrainingLevel => {
  if (Array.isArray(trainingLevel)) {
    return resolvePrimaryWorkoutLevel(trainingLevel) ?? 'beginner';
  }

  return trainingLevel || 'beginner';
};

const buildLoadSummary = (items: TrainingBlockExercise[], trainingLevel: TrainingLevelInput) => {
  if (items.length === 0) {
    return {
      loadPercentage: '0%',
      loadIntensity: '- -',
      effortPerception: '- -',
    };
  }

  const explicitLoadValues = items.flatMap((exercise) => resolveExerciseLoadValues(exercise));

  if (explicitLoadValues.length > 0) {
    const loadPercentageValue = clamp(
      Math.round(explicitLoadValues.reduce((sum, value) => sum + value, 0) / explicitLoadValues.length),
      0,
      100,
    );
    const loadIntensity = loadPercentageValue >= 72 ? 'Alta' : loadPercentageValue >= 55 ? 'Moderada' : 'Leve';
    const effortScore = clamp(Math.round((Math.max(loadPercentageValue, 1) / 10) + 2), 3, 9);
    const effortPerception = effortScore >= 7 ? 'Intensa' : effortScore >= 5 ? 'Moderada' : 'Leve';

    return {
      loadPercentage: `${loadPercentageValue}%`,
      loadIntensity,
      effortPerception,
    };
  }

  const methodAverage = items.reduce((sum, exercise) => sum + resolveMethodLoadFactor(exercise.execution.method), 0) / items.length;
  const setsAverage = items.reduce((sum, exercise) => sum + Math.max(resolveExecutionDetails(exercise.execution).length, parsePositiveInteger(exercise.execution.sets, 3)), 0) / items.length;
  const baseLoadPercentage = LEVEL_LOAD_BASE[resolveLoadTrainingLevel(trainingLevel)] ?? LEVEL_LOAD_BASE.beginner;
  const loadPercentageValue = clamp(
    Math.round(baseLoadPercentage + ((setsAverage - 3) * 5) + ((methodAverage - 1) * 30)),
    35,
    90,
  );
  const loadIntensity = loadPercentageValue >= 72 ? 'Alta' : loadPercentageValue >= 55 ? 'Moderada' : 'Leve';
  const effortScore = clamp(Math.round((loadPercentageValue / 10) + (methodAverage * 2)), 3, 9);
  const effortPerception = effortScore >= 7 ? 'Intensa' : effortScore >= 5 ? 'Moderada' : 'Leve';

  return {
    loadPercentage: `${loadPercentageValue}%`,
    loadIntensity,
    effortPerception,
  };
};

const formatSeriesValue = (count: number) => `${count} ${count === 1 ? 'série' : 'séries'}`;

const formatTotalVolumeValue = (count: number) => String(count);

export const resolveTrainingExerciseMuscleGroups = (group: string, explicitGroups: string[] = []) => {
  const normalizedGroups = explicitGroups
    .map((item) => normalizeMuscleGroupLabel(item))
    .filter((item): item is string => Boolean(item));

  if (normalizedGroups.length > 0) {
    return Array.from(new Set(normalizedGroups));
  }

  const mappedGroups = EXERCISE_GROUP_TO_MUSCLE_GROUPS[normalizeText(group)] ?? [];

  if (mappedGroups.length > 0) {
    return mappedGroups;
  }

  const normalizedGroup = normalizeMuscleGroupLabel(group);
  return normalizedGroup ? [normalizedGroup] : ['Não informado'];
};

export const buildTrainingBlockSummary = (block: TrainingBlock, trainingLevel: TrainingLevelInput) => {
  if (block.items.length === 0) {
    return {
      totalVolume: '0',
      totalTime: '00min00s',
      loadPercentage: '0%',
      loadIntensity: '- -',
      effortPerception: '- -',
    };
  }

  const totalVolume = block.items.reduce((sum, exercise) => sum + resolveVolumeContribution(exercise), 0);

  return {
    totalVolume: formatTotalVolumeValue(totalVolume),
    totalTime: '00min00s',
    ...buildLoadSummary(block.items, trainingLevel),
  };
};

export const buildTrainingSummary = (blocks: TrainingBlock[], trainingLevel: TrainingLevelInput): {
  metrics: TrainingSummaryMetric[];
  muscleVolumes: TrainingMuscleVolume[];
  totalVolume: string;
  totalTime: string;
  loadPercentage: string;
  loadIntensity: string;
  effortPerception: string;
} => {
  const exercises = blocks.flatMap((block) => block.items);
  const totalVolume = exercises.reduce((sum, exercise) => sum + resolveVolumeContribution(exercise), 0);
  const totalTime = '00min00s';
  const loadSummary = buildLoadSummary(exercises, trainingLevel);
  const muscleSetTotals = TRAINING_MUSCLE_GROUP_LABELS.reduce<Record<string, number>>((accumulator, label) => {
    accumulator[label] = 0;
    return accumulator;
  }, {});

  exercises.forEach((exercise) => {
    const sets = resolveVolumeContribution(exercise);
    const muscleGroups = resolveTrainingExerciseMuscleGroups(exercise.group, exercise.muscleGroups);

    muscleGroups.forEach((muscleGroup) => {
      if (muscleSetTotals[muscleGroup] === undefined) {
        muscleSetTotals[muscleGroup] = 0;
      }

      muscleSetTotals[muscleGroup] += sets;
    });
  });

  return {
    totalVolume: formatTotalVolumeValue(totalVolume),
    totalTime,
    ...loadSummary,
    metrics: [
      { label: 'Volume total', value: formatTotalVolumeValue(totalVolume) },
      { label: 'Tempo total', value: totalTime },
      { label: 'Carga', value: loadSummary.loadPercentage },
      { label: 'Intensidade', value: loadSummary.loadIntensity },
      { label: 'Percepção de esforço', value: loadSummary.effortPerception },
    ],
    muscleVolumes: TRAINING_MUSCLE_GROUP_LABELS.map((label) => ({
      label,
      value: formatSeriesValue(muscleSetTotals[label] ?? 0),
    })),
  };
};

export const normalizeTrainingExecutionMetadata = (
  execution: TrainingExerciseExecution,
  metadata: {
    executionSpeed?: TrainingExerciseExecution['executionSpeed'];
    isometricSeconds?: number | null;
    concentricSeconds?: number | null;
    pauseSeconds?: number | null;
    eccentricSeconds?: number | null;
    executionSeconds?: number | null;
    exerciseType?: ExerciseType | null;
  },
): TrainingExerciseExecution => ({
  ...execution,
  executionSpeed: metadata.executionSpeed ?? execution.executionSpeed ?? null,
  isometricSeconds: metadata.isometricSeconds ?? execution.isometricSeconds ?? null,
  concentricSeconds: metadata.concentricSeconds ?? execution.concentricSeconds ?? null,
  pauseSeconds: metadata.pauseSeconds ?? execution.pauseSeconds ?? null,
  eccentricSeconds: metadata.eccentricSeconds ?? execution.eccentricSeconds ?? null,
  executionSeconds: metadata.executionSeconds ?? execution.executionSeconds ?? null,
  exerciseType: metadata.exerciseType ?? execution.exerciseType ?? null,
});
